import moment from "moment";
import { displayDate } from "../formatting/displayDate";
import { htmlToPlainText, tiptapToPlainText } from "../input/TipTap/tiptapToPlainText";
import { displayDateWithTime } from "../formatting/displayDateWithTime";
import type { SupportedLocale } from "../formatting/displayDate";
import { STEP_DOCUMENT_PART_TOOL_NAME } from "./stepDocument";

export type LeafEntry = { path: string[]; value: unknown; cleared?: boolean; previous?: unknown };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const ENUM_VALUE = /^[A-Z][A-Z0-9_]+$/;

const inNorwegian = (value: string, locale?: SupportedLocale | null) => {
  const date = locale ? moment(value).locale(locale) : moment(value);
  return date.isValid() && date.locale() === "nb" ? date : null;
};

const cardDate = (value: string, locale?: SupportedLocale | null): string =>
  inNorwegian(value, locale)?.format("DD. MMM YYYY") ?? displayDate(value, locale);

const cardDateTime = (value: string, locale?: SupportedLocale | null): string =>
  inNorwegian(value, locale)?.format("DD. MMM YYYY, HH:mm") ?? displayDateWithTime(value, null, locale);

// A human label for an indexed entity, used both to show what an id in a tool's args refers to
// (ConfirmationCard) and to label an entity highlight card (entityHighlights). Shared here so both
// read the same field priority — never duplicate this list.
export const ENTITY_LABEL_KEYS = ["supplierName", "title", "name", "subject", "referenceId"] as const;

export const entityLabel = (entity: Record<string, unknown> | undefined): string => {
  if (!entity) {
    return "";
  }
  for (const key of ENTITY_LABEL_KEYS) {
    const value = entity[key];
    if (typeof value === "string" && value) {
      return value;
    }
  }
  return "";
};

export const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const moneyAmount = (value: Record<string, unknown>): number | undefined =>
  typeof value.amount === "number" ? value.amount : typeof value.value === "number" ? value.value : undefined;

const isMoneyShape = (value: Record<string, unknown>): boolean =>
  moneyAmount(value) !== undefined && typeof value.currency === "string";

export const hasLeafValue = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return true;
  }
  return value != null;
};

// A tool's body args mirror its OpenAPI schema, so update-style fields nest arbitrarily deep
// (e.g. counterparty.counterparty.organizationEntryId). Flatten to one entry per leaf so the card
// can render a row per actual value instead of a JSON blob. Money shapes ({amount|value, currency})
// stay a single leaf — they render as one composite "amount currency" row, not two.
export const flattenLeaves = (path: string[], value: unknown): LeafEntry[] => {
  if (value === null || (Array.isArray(value) && value.length === 0)) {
    return [{ path, value, cleared: true }];
  }
  if (isPlainObject(value)) {
    if (ONE_ROW_KEYS.has(path[path.length - 1])) {
      return [{ path, value }];
    }
    if (isMoneyShape(value)) {
      return hasLeafValue(moneyAmount(value)) ? [{ path, value }] : [];
    }
    return Object.entries(value).flatMap(([key, nested]) => flattenLeaves([...path, key], nested));
  }
  return hasLeafValue(value) ? [{ path, value }] : [];
};

const PATH_LABEL_KEYS: Record<string, string> = {
  "startDate.date": "Start date",
  "endDate.date": "End date",
  "selectionCriteria.type": "Category",
};

const TOOL_LEAF_LABEL_KEYS: Record<string, Record<string, string>> = {
  [STEP_DOCUMENT_PART_TOOL_NAME]: { chapters: "Chapters this part adds" },
};

const LEAF_LABEL_KEYS: Record<string, string> = {
  answer: "Answer",
  organizationNumber: "Organization number",
  street: "Street address",
  postCode: "Post code",
  city: "City",
  title: "Title",
  description: "Description",
  internalNote: "Internal note",
  winningBidIds: "Winning bids",
  estimatedValue: "Estimated value",
  eventValue: "Estimated value",
  contractNature: "Contract type",
  organizationId: "Counterparty",
  organizationEntryId: "Counterparty",
  amount: "Value",
  hasNoEndDate: "No end date",
  personId: "Responsible",
  name: "Name",
  category: "Category",
  instructions: "What the document will contain",
  chapters: "Chapters",
  chaptersNow: "Chapters written now",
  shortChapters: "Chapters written shorter",
  startDate: "Start date",
  endDate: "End date",
  externalId: "Project ID",
  bidSubmissionDeadline: "Bid deadline",
  requestDeadline: "Request deadline",
  questionDaysBeforeBid: "Question deadline, days before the bid deadline",
  referenceId: "Reference id",
  cpvCodes: "CPV codes",
  procedureType: "Procedure",
  regulation: "Regulation",
  contractDuration: "Contract duration",
  placeOfPerformance: "Place of performance",
  selectionCriteria: "Qualification requirements",
  minimumRequirements: "Minimum requirements",
  awardCriteria: "Award criteria",
  organizationInvites: "Invited suppliers",
  frameworkAgreement: "Framework agreement",
  dynamicPurchasingSystem: "Dynamic purchasing system",
  electronicAuction: "Electronic auction",
  priceModel: "Price model",
  activeExclusionGroundIds: "Exclusion grounds",
  utilityActivity: "Utility activity",
  serviceCategory: "What kind of service is the contract?",
  specialServices: "Special services",
  includeProducts: "Products",
  includeMinimumRequirements: "Minimum requirements",
  includeAwardCriteria: "Award criteria",
  requiresEvidence: "Requires documentation",
  foaPart: "Part of the procurement regulations",
  periodMonths: "Contract period in months",
  type: "Type",
  weight: "Weight",
  responseType: "Answer type",
  answerFormat: "Answer format",
  requiresDocumentation: "Requires documentation",
  commonRationale: "Rationale",
  environmentWeightingJustification: "Justification for the environmental weighting",
  tenderValidityMonths: "Tender validity",
  acceleratedProcedure: "Accelerated procedure",
  subCriteria: "Sub-criteria",
  tags: "Tags",
  reservedParticipation: "Reserved participation",
  announcementDate: "Announcement date",
  criteria: "Criteria",
  lowestPrice: "Lowest price",
  bestPriceQualityRatio: "Best price-quality ratio",
  rationale: "Rationale",
  hasOptions: "Options in the contract",
  procedureJustification: "Justification for the procedure",
  euFunded: "EU funding",
  submissionLanguages: "Languages for tenders",
  variants: "Alternative tenders",
  climateConsideration: "Climate consideration",
  maxCandidates: "Maximum number of candidates",
  minCandidates: "Minimum number of candidates",
  frameworkMaximumValue: "Maximum value of the framework agreement",
  descriptions: "Option descriptions",
  bidSubmissionDeadlineConfirmed: "Bid deadline confirmed",
  requestDeadlineConfirmed: "Request deadline confirmed",
  tenderDeadlineAgreedWithCandidates: "Tender deadline agreed with the candidates",
  announcePartIOnDoffin: "Announce on Doffin",
  extensions: "Extensions",
};

const DEADLINE_CONFIRMED_BY: Record<string, string> = {
  bidSubmissionDeadlineConfirmed: "bidSubmissionDeadline",
  requestDeadlineConfirmed: "requestDeadline",
};

export const confirmedDeadlinePath = (entry: LeafEntry): string[] | null => {
  const deadline = DEADLINE_CONFIRMED_BY[entry.path[entry.path.length - 1]];
  return entry.value === true && deadline ? [...entry.path.slice(0, -1), deadline] : null;
};

const PROCUREMENT_ROOTS = new Set(["eventData", "publicContext", "privateContext", "miniCompetitionContext", "eventContext"]);

const PROCUREMENT_LABEL_KEYS: Record<string, string> = {
  options: "Options in the contract",
  milestonePlan: "Schedule",
  projects: "Projects",
};

const procurementLabelKey = (path: string[]): string | undefined =>
  path.length >= 2 && PROCUREMENT_ROOTS.has(path[path.length - 2])
    ? PROCUREMENT_LABEL_KEYS[path[path.length - 1]]
    : undefined;

export const isLabelled = (path: string[]): boolean =>
  path.join(".") in PATH_LABEL_KEYS ||
  path[path.length - 1] in LEAF_LABEL_KEYS ||
  !!procurementLabelKey(path) ||
  extensionFieldOf(path) !== null;

const ONE_ROW_KEYS = new Set(["awardCriteria", "contractDuration", "priceModel"]);

// Returns a label KEY, not display text — callers look this up in a literal t(...) map (see
// ConfirmationCard's useLeafLabel) so langen can still statically extract every translation key.
export const leafLabelKey = (path: string[], toolName?: string): string => {
  const joined = path.join(".");
  const lastSegment = path[path.length - 1];
  return (
    (toolName ? TOOL_LEAF_LABEL_KEYS[toolName]?.[joined] : undefined) ??
    PATH_LABEL_KEYS[joined] ??
    LEAF_LABEL_KEYS[lastSegment] ??
    procurementLabelKey(path) ??
    lastSegment
  );
};

export type FormatLeafOptions = {
  locale: SupportedLocale;
  yesLabel: string;
  noLabel: string;
  clearedLabel: string;
  lowestPriceLabel?: string;
  bestPriceQualityRatioLabel?: string;
  monthsLabel?: (count: number) => string;
  periodFromLabel?: (period: string, startDate: string) => string;
  extensionLabel?: (extension: Record<string, unknown>, number: number) => string;
  monthsBeforeEndLabel?: (count: number) => string;
  translateEnum?: (value: string) => string | undefined;
  resolveEntityName: (id: string) => string | undefined;
};

export const formatLeafValue = (entry: LeafEntry, opts: FormatLeafOptions): string => {
  const { value } = entry;
  if (entry.cleared) {
    return opts.clearedLabel;
  }
  if (isExtensionList(entry) && opts.extensionLabel) {
    const label = opts.extensionLabel;
    return entry.value.map((extension, index) => label(extension, index + 1)).join("\n");
  }
  if (Array.isArray(value)) {
    return value.map((item) => listItemLabel(item, opts)).join(", ");
  }
  if (isPlainObject(value) && entry.path[entry.path.length - 1] === "awardCriteria") {
    return awardCriteriaLabel(value, opts);
  }
  if (isPlainObject(value) && entry.path[entry.path.length - 1] === "contractDuration") {
    return contractDurationLabel(value, opts);
  }
  if (isPlainObject(value) && entry.path[entry.path.length - 1] === "priceModel") {
    return priceModelLabel(value, opts);
  }
  if (isPlainObject(value) && isMoneyShape(value)) {
    const amount = moneyAmount(value) ?? 0;
    return `${amount.toLocaleString("nb-NO")} ${value.currency as string}`;
  }
  if (typeof value === "boolean") {
    return value ? opts.yesLabel : opts.noLabel;
  }
  if (typeof value === "number" && extensionFieldOf(entry.path)?.field === "exerciseDeadlineMonths" && opts.monthsBeforeEndLabel) {
    return opts.monthsBeforeEndLabel(value);
  }
  if (typeof value === "number") {
    return value.toLocaleString("nb-NO");
  }
  if (typeof value === "string") {
    const key = entry.path[entry.path.length - 1];
    if (key.endsWith("Id")) {
      return opts.resolveEntityName(value) ?? value;
    }
    if (ISO_DATE_TIME.test(value)) {
      return cardDateTime(value, opts.locale);
    }
    if (ISO_DATE.test(value)) {
      return cardDate(value, opts.locale);
    }
    if (ENUM_VALUE.test(value)) {
      return opts.translateEnum?.(value) ?? value;
    }
    return value;
  }
  return value == null ? "" : String(value);
};

export const formatChangedLeafValue = (entry: LeafEntry, opts: FormatLeafOptions): string => {
  const value = formatLeafValue(entry, opts);
  if (!("previous" in entry)) return value;
  const previous = hasLeafValue(entry.previous) ? formatLeafValue({ path: entry.path, value: entry.previous }, opts) : "–";
  return `${previous} → ${value}`;
};

const priceModelLabel = (value: Record<string, unknown>, opts: FormatLeafOptions): string => {
  const chosen = Object.entries(value).find(([, model]) => isPlainObject(model));
  if (!chosen) return "";
  const [kind, model] = chosen as [string, Record<string, unknown>];
  const name = opts.translateEnum?.(kind.toUpperCase()) ?? kind;
  return typeof model.breakpointPercent === "number" ? `${name} (${model.breakpointPercent} %)` : name;
};

const LIST_ITEM_LABEL_KEYS = ["name", "title", "code", "text", "description"] as const;

type ListLabelOptions = Pick<FormatLeafOptions, "resolveEntityName">;

export const listItemLabel = (item: unknown, opts: ListLabelOptions): string => {
  if (typeof item === "string") {
    return opts.resolveEntityName(item) ?? item;
  }
  if (!isPlainObject(item)) {
    return String(item);
  }
  if (typeof item.organizationId === "string") {
    const name = typeof item.organizationName === "string" ? item.organizationName : undefined;
    return opts.resolveEntityName(item.organizationId) ?? name ?? item.organizationId;
  }
  for (const key of LIST_ITEM_LABEL_KEYS) {
    const label = item[key];
    if (typeof label === "string" && label.trim()) {
      return label;
    }
  }
  return typeof item.id === "string" ? item.id : JSON.stringify(item);
};

const listItemKey = (item: unknown): string => {
  if (!isPlainObject(item)) {
    return String(item);
  }
  for (const key of ["organizationId", "id", ...LIST_ITEM_LABEL_KEYS]) {
    const value = item[key];
    if (typeof value === "string" && value) {
      return `${key}:${value}`;
    }
  }
  return JSON.stringify(item);
};

export const addedItemFields = (item: unknown): LeafEntry[] => {
  if (!isPlainObject(item) || typeof item.organizationId === "string") return [];
  const labelKey = LIST_ITEM_LABEL_KEYS.find((key) => typeof item[key] === "string" && item[key].trim());
  return Object.entries(item)
    .filter(([key]) => key !== "id" && key !== labelKey)
    .flatMap(([key, value]) => flattenLeaves([key], value).filter((field) => !field.cleared));
};

export type ChangedItem = { item: unknown; fields: LeafEntry[] };

export type ListChange = { added: unknown[]; removed: unknown[]; changed: ChangedItem[] };

export const listChange = (proposed: unknown[], current: unknown[]): ListChange => {
  const proposedKeys = new Set(proposed.map(listItemKey));
  const currentByKey = new Map(current.map((item) => [listItemKey(item), item]));
  const changed = proposed.flatMap((item): ChangedItem[] => {
    const before = currentByKey.get(listItemKey(item));
    if (before === undefined || !isPlainObject(item) || !isPlainObject(before)) return [];
    const fields = Object.entries(item)
      .filter(([key, value]) => !sameValue(value, before[key]))
      .flatMap(([key, value]) =>
        typeof value === "string" && !value.trim()
          ? [{ path: [key], value, cleared: true }]
          : flattenLeaves([key], value).map((field) => withPrevious(field, before[key])),
      );
    return fields.length > 0 ? [{ item, fields }] : [];
  });
  return {
    added: proposed.filter((item) => !currentByKey.has(listItemKey(item))),
    removed: current.filter((item) => !proposedKeys.has(listItemKey(item))),
    changed,
  };
};

const FULL_TEXT_KEYS = new Set(["description", "commonRationale"]);

const isScalar = (value: unknown): boolean =>
  value == null || typeof value === "string" || typeof value === "number" || typeof value === "boolean";

const withPrevious = (field: LeafEntry, previous: unknown): LeafEntry =>
  field.path.length === 1 &&
  !field.cleared &&
  !FULL_TEXT_KEYS.has(field.path[0]) &&
  isScalar(field.value) &&
  isScalar(previous) &&
  !(typeof field.value === "string" && isRichTextDocument(field.value))
    ? { ...field, previous }
    : field;

const sameValue = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const contractDurationLabel = (value: Record<string, unknown>, opts: FormatLeafOptions): string => {
  if (typeof value.periodMonths === "number") {
    const period = opts.monthsLabel?.(value.periodMonths) ?? String(value.periodMonths);
    const starts = typeof value.startDate === "string" && ISO_DATE.test(value.startDate) ? value.startDate : null;
    return starts && opts.periodFromLabel ? opts.periodFromLabel(period, cardDate(starts, opts.locale)) : period;
  }
  const dates = [value.startDate, value.endDate]
    .filter((date): date is string => typeof date === "string" && ISO_DATE.test(date))
    .map((date) => cardDate(date, opts.locale));
  return dates.length > 0 ? dates.join(" – ") : (opts.translateEnum?.(String(value.type)) ?? String(value.type ?? ""));
};

const READ_SIDE_OF_PATCH_ROOT: Record<string, string> = {
  publicContext: "eventContext",
  privateContext: "eventContext",
  miniCompetitionContext: "eventContext",
};

export const currentValueAt = (current: Record<string, unknown> | undefined, path: string[]): unknown => {
  if (!current || path.length === 0) {
    return undefined;
  }
  const [root, ...rest] = path;
  return [READ_SIDE_OF_PATCH_ROOT[root] ?? root, ...rest].reduce<unknown>(
    (node, key) => (isPlainObject(node) ? node[key] : undefined),
    current,
  );
};

const awardCriteriaLabel = (value: Record<string, unknown>, opts: FormatLeafOptions): string => {
  const plan = awardCriteriaPlan(value, undefined, opts);
  if (plan.kind === "lowestPrice") return plan.heading;
  const named = plan.rows.map((row) => (row.weight ? `${row.label} (${row.weight})` : row.label));
  return named.length > 0 ? `${plan.heading}: ${named.join(", ")}` : plan.heading;
};

export type SubCriterionChange = {
  label: string;
  change: "added" | "removed" | "changed";
  newDescription: string | null;
  details: LeafEntry[];
  shareOfTotal?: string;
};

export type AwardCriterionRow = {
  label: string;
  weight: string | null;
  previousWeight: string | null;
  change: "added" | "removed" | "reweighted" | "changed" | "unchanged";
  newDescription: string | null;
  descriptionCleared: boolean;
  details: LeafEntry[];
  subCriteria: SubCriterionChange[];
};

export type AwardCriteriaPlan =
  | { kind: "lowestPrice"; heading: string }
  | { kind: "bestPriceQualityRatio"; heading: string; rows: AwardCriterionRow[] };

export const criterionLabel = (
  criterion: Record<string, unknown>,
  translateEnum?: (value: string) => string | undefined,
): string =>
  typeof criterion.name === "string" && criterion.name.trim()
    ? criterion.name
    : (translateEnum?.(String(criterion.type)) ?? String(criterion.type ?? ""));

export const awardCriteriaPlan = (
  proposed: Record<string, unknown>,
  current: unknown,
  opts: Pick<FormatLeafOptions, "lowestPriceLabel" | "bestPriceQualityRatioLabel" | "translateEnum">,
): AwardCriteriaPlan => {
  const lowest = proposed.lowestPrice;
  if (isPlainObject(lowest) && lowest.selected === true) {
    return { kind: "lowestPrice", heading: opts.lowestPriceLabel ?? "lowestPrice" };
  }
  const heading = opts.bestPriceQualityRatioLabel ?? "bestPriceQualityRatio";
  const proposedCriteria = criteriaOf(proposed);
  const currentCriteria = isPlainObject(current) ? criteriaOf(current) : null;
  const label = (criterion: Record<string, unknown>) => criterionLabel(criterion, opts.translateEnum);
  const rows = proposedCriteria.map((criterion): AwardCriterionRow => {
    const weight = weightLabel(criterion.weight);
    const description = descriptionOf(criterion);
    const before = currentCriteria?.find((candidate) => sameCriterion(candidate, criterion));
    if (!currentCriteria || !before) {
      return {
        label: label(criterion),
        weight,
        previousWeight: null,
        change: currentCriteria ? "added" : "unchanged",
        newDescription: description,
        descriptionCleared: false,
        details: newCriterionDetails(criterion),
        subCriteria: subCriterionChanges(criterion, null),
      };
    }
    const previousWeight = weightLabel(before.weight);
    const previousDescription = descriptionOf(before);
    const newDescription = sameText(description, previousDescription) ? null : description;
    const descriptionCleared = previousDescription !== null && description === null;
    const details = changedCriterionFields(criterion, before);
    const edited = details.length > 0 || newDescription !== null || descriptionCleared;
    return {
      label: label(criterion),
      weight,
      previousWeight,
      change: previousWeight !== weight ? "reweighted" : edited ? "changed" : "unchanged",
      newDescription,
      descriptionCleared,
      details,
      subCriteria: subCriterionChanges(criterion, before),
    };
  });
  const removed = (currentCriteria ?? [])
    .filter((criterion) => !proposedCriteria.some((candidate) => sameCriterion(criterion, candidate)))
    .map((criterion): AwardCriterionRow => ({
      label: label(criterion),
      weight: weightLabel(criterion.weight),
      previousWeight: null,
      change: "removed",
      newDescription: null,
      descriptionCleared: false,
      details: [],
      subCriteria: [],
    }));
  return { kind: "bestPriceQualityRatio", heading, rows: [...rows, ...removed] };
};

const subCriteriaOf = (criterion: Record<string, unknown>): Record<string, unknown>[] =>
  Array.isArray(criterion.subCriteria) ? criterion.subCriteria.filter(isPlainObject) : [];

const subCriterionChanges = (
  proposed: Record<string, unknown>,
  before: Record<string, unknown> | null,
): SubCriterionChange[] => {
  const name = (subCriterion: Record<string, unknown>) => String(subCriterion.name ?? "").trim();
  const previous = before ? subCriteriaOf(before) : [];
  const proposedSubCriteria = subCriteriaOf(proposed);
  const withShare = (subCriterion: Record<string, unknown>, details: LeafEntry[]) => {
    const share = details.some((field) => field.path[0] === "weight") ? shareOfTotal(proposed.weight, subCriterion.weight) : null;
    return share ? { shareOfTotal: share } : {};
  };
  const changed = proposedSubCriteria.flatMap((subCriterion): SubCriterionChange[] => {
    const match = previous.find((candidate) => sameCriterion(candidate, subCriterion));
    const description = descriptionOf(subCriterion);
    if (!match) {
      const details = newSubCriterionDetails(subCriterion);
      return [{ label: name(subCriterion), change: "added", newDescription: description, details, ...withShare(subCriterion, details) }];
    }
    const newDescription = sameText(description, descriptionOf(match)) ? null : description;
    const details = changedFields(subCriterion, match, SUB_CRITERION_FIELDS);
    return newDescription === null && details.length === 0
      ? []
      : [{ label: name(subCriterion), change: "changed", newDescription, details, ...withShare(subCriterion, details) }];
  });
  const removed = previous
    .filter((subCriterion) => !proposedSubCriteria.some((candidate) => sameCriterion(subCriterion, candidate)))
    .map((subCriterion): SubCriterionChange => ({ label: name(subCriterion), change: "removed", newDescription: null, details: [] }));
  return [...changed, ...removed];
};

const SUB_CRITERION_FIELDS = ["name", "weight", "answerFormat", "requiresDocumentation"];

const changedCriterionFields = (proposed: Record<string, unknown>, before: Record<string, unknown>): LeafEntry[] => [
  ...changedFields(proposed, before, ["name", "type"]),
  ...(proposed.commonRationale !== undefined && !sameText(textOf(proposed.commonRationale), textOf(before.commonRationale))
    ? flattenLeaves(["commonRationale"], textOf(proposed.commonRationale))
    : []),
];

const textOf = (value: unknown): string | null => (typeof value === "string" && value.trim() ? value : null);

const comparable = (key: string, value: unknown): unknown => {
  if (key === "weight") return weightLabel(value);
  if (typeof value === "string") return value.trim() || null;
  return value ?? null;
};

const changedFields = (proposed: Record<string, unknown>, before: Record<string, unknown>, keys: string[]): LeafEntry[] =>
  keys.flatMap((key): LeafEntry[] => {
    if (proposed[key] === undefined) return [];
    const value = comparable(key, proposed[key]);
    const previous = comparable(key, before[key]);
    if (value === previous) return [];
    return [{ path: [key], value, previous, ...(value === null ? { cleared: true } : {}) }];
  });

const newCriterionDetails = (criterion: Record<string, unknown>): LeafEntry[] => {
  const named = typeof criterion.name === "string" && criterion.name.trim();
  return [
    ...(named ? flattenLeaves(["type"], criterion.type) : []),
    ...flattenLeaves(["commonRationale"], criterion.commonRationale),
  ].filter((field) => !field.cleared);
};

const newSubCriterionDetails = (subCriterion: Record<string, unknown>): LeafEntry[] => {
  const weight = weightLabel(subCriterion.weight);
  return [
    ...(weight ? [{ path: ["weight"], value: weight }] : []),
    ...flattenLeaves(["answerFormat"], subCriterion.answerFormat),
    ...flattenLeaves(["requiresDocumentation"], subCriterion.requiresDocumentation),
  ].filter((field) => !field.cleared);
};

const criteriaOf = (awardCriteria: Record<string, unknown>): Record<string, unknown>[] => {
  const ratio = awardCriteria.bestPriceQualityRatio;
  return isPlainObject(ratio) && Array.isArray(ratio.criteria) ? ratio.criteria.filter(isPlainObject) : [];
};

export const sameCriterion = (a: Record<string, unknown>, b: Record<string, unknown>): boolean => {
  if (typeof a.id === "string" && typeof b.id === "string") return a.id === b.id;
  if (typeof a.name === "string" && typeof b.name === "string") return a.name.trim() === b.name.trim();
  return a.type === b.type && !a.name && !b.name;
};

const weightLabel = (weight: unknown): string | null => {
  if (!isPlainObject(weight)) return null;
  const amount = Number(weight.number);
  if (!Number.isFinite(amount)) return null;
  const formatted = amount.toLocaleString("nb-NO");
  return weight.type === "PERCENTAGE" ? `${formatted} %` : formatted;
};

const percentageOf = (weight: unknown): number | null => {
  if (!isPlainObject(weight) || weight.type !== "PERCENTAGE") return null;
  const amount = Number(weight.number);
  return Number.isFinite(amount) ? amount : null;
};

const shareOfTotal = (criterionWeight: unknown, subCriterionWeight: unknown): string | null => {
  const criterion = percentageOf(criterionWeight);
  const own = percentageOf(subCriterionWeight);
  if (criterion === null || own === null) return null;
  return ((criterion * own) / 100).toLocaleString("nb-NO", { maximumFractionDigits: 1 });
};

export const sameText = (a: string | null, b: string | null): boolean => wordsOf(a) === wordsOf(b);

const wordsOf = (text: string | null): string =>
  visibleText(text)
    .replace(/(^|\s)(?:[-*•]|\d+[.)])(?=\s)/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const visibleText = (text: string | null): string =>
  text !== null && isRichTextDocument(text) ? tiptapToPlainText(text) : htmlToPlainText(text);

const descriptionOf = (criterion: Record<string, unknown>): string | null =>
  typeof criterion.description === "string" && criterion.description.trim() ? criterion.description : null;

export const isRichTextDocument = (text: string): boolean => {
  if (!text.trimStart().startsWith("{")) return false;
  try {
    const parsed: unknown = JSON.parse(text);
    return isPlainObject(parsed) && parsed.type === "doc";
  } catch {
    return false;
  }
};

export type ExtensionField = {
  number: number | null;
  field: "extension" | "duration" | "unit" | "exerciseDeadlineMonths";
};

const EXTENSION_FIELDS = new Set(["duration", "unit", "exerciseDeadlineMonths"]);

export const extensionFieldOf = (path: string[]): ExtensionField | null => {
  const at = path.lastIndexOf("extensions");
  if (at < 0) return null;
  const rest = path.slice(at + 1);
  const number = rest.length > 0 && /^\d+$/.test(rest[0]) ? Number(rest[0]) + 1 : null;
  const after = number === null ? rest : rest.slice(1);
  if (after.length === 0) return number === null ? null : { number, field: "extension" };
  if (after.length !== 1 || !EXTENSION_FIELDS.has(after[0])) return null;
  return { number, field: after[0] as ExtensionField["field"] };
};

export const isExtensionList = (entry: LeafEntry): entry is LeafEntry & { value: Record<string, unknown>[] } =>
  entry.path[entry.path.length - 1] === "extensions" &&
  !entry.cleared &&
  Array.isArray(entry.value) &&
  entry.value.every(isPlainObject);
